/**
 * MurtiLeb storefront helpers:
 *  - quick add from product cards through Horizon's cart events (opens the cart drawer)
 *  - wishlist kept in localStorage (no app), rendered on /pages/wishlist
 *  - "Read more" toggles for long collection descriptions
 * Everything uses delegated listeners so cards added by infinite scroll or section morphs work.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/* Keep the mobile tab bar's cart badge in sync with every cart change (cards, product page, drawer). */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail }) => {
      const count = cart?.totalQuantity ?? detail?.items?.reduce((sum, item) => sum + item.quantity, 0);
      if (typeof count !== 'number') return;
      document.querySelectorAll('[data-ml-cart-count]').forEach((badge) => {
        badge.textContent = String(count);
      });
    })
    .catch(() => {});
});

const WISHLIST_KEY = 'murtileb:wishlist';

const readWishlist = () => {
  try {
    const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const writeWishlist = (list) => {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable (private mode) — wishlist stays session-only */
  }
  document.dispatchEvent(new CustomEvent('murtileb:wishlist', { detail: { list } }));
};

const syncWishButtons = (root = document) => {
  const list = readWishlist();
  root.querySelectorAll('[data-ml-wish]').forEach((button) => {
    const saved = list.includes(button.dataset.mlWish);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
  });
  document.querySelectorAll('[data-ml-wish-count]').forEach((el) => {
    el.textContent = list.length ? String(list.length) : '';
    el.hidden = list.length === 0;
  });
};

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-ml-wish]') : null;
  if (!button) return;
  event.preventDefault();
  const handle = button.dataset.mlWish;
  const list = readWishlist();
  const next = list.includes(handle) ? list.filter((h) => h !== handle) : [handle, ...list];
  writeWishlist(next);
  syncWishButtons();
});

/* ---------- Quick add ---------- */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ml-quick-add')) return;
  // Horizon declares `Theme` with `const`, so it is a global binding but not a window property.
  if (typeof Theme === 'undefined' || !Theme.routes?.cart_add_url) return; // fall back to normal submit

  event.preventDefault();
  const button = form.querySelector('button[type="submit"]');
  if (!button || button.getAttribute('aria-busy') === 'true') return;

  const label = button.textContent;
  button.setAttribute('aria-busy', 'true');
  button.textContent = 'Adding…';

  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const result = await response.json();
    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );

    if (result.status) {
      form.dispatchEvent(new CartErrorEvent({ error: result.description || result.message || 'Add to cart failed', code: 'INVALID' }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'murtileb-card', itemCount: quantity },
      });
      button.textContent = result.description || 'Unavailable';
    } else {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, source: 'murtileb-card', itemCount: quantity },
      });
      button.textContent = 'Added ✓';
    }
  } catch (error) {
    deferred.reject(error);
    form.submit(); // network problem: let the browser do a normal add-to-cart
    return;
  }

  window.setTimeout(() => {
    button.textContent = label;
    button.removeAttribute('aria-busy');
  }, 1800);
});

/* ---------- Wishlist page ---------- */
const renderWishlistPage = async () => {
  const container = document.querySelector('[data-ml-wishlist]');
  if (!container) return;
  const grid = container.querySelector('[data-ml-wishlist-grid]');
  const empty = container.querySelector('[data-ml-wishlist-empty]');
  const list = readWishlist();

  if (!list.length) {
    grid.innerHTML = '';
    empty.hidden = false;
    return;
  }
  empty.hidden = true;

  // Render each saved product with the theme's own card via the section rendering API.
  const cards = await Promise.all(
    list.map((handle) =>
      fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?section_id=ml-card-render`)
        .then((r) => (r.ok ? r.text() : ''))
        .catch(() => '')
    )
  );

  const valid = [];
  grid.innerHTML = '';
  cards.forEach((html, i) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const card = doc.querySelector('.ml-card');
    if (!card) return;
    valid.push(list[i]);
    const li = document.createElement('li');
    li.append(card);
    grid.append(li);
  });

  // Drop products that no longer exist.
  if (valid.length !== list.length) writeWishlist(valid);
  empty.hidden = valid.length > 0;
  syncWishButtons(grid);
};

document.addEventListener('murtileb:wishlist', () => {
  const container = document.querySelector('[data-ml-wishlist]');
  if (!container) return;
  // Remove un-saved cards immediately without refetching.
  const list = readWishlist();
  container.querySelectorAll('[data-ml-wish]').forEach((button) => {
    if (!list.includes(button.dataset.mlWish)) button.closest('li')?.remove();
  });
  container.querySelector('[data-ml-wishlist-empty]').hidden = list.length > 0;
});

/* ---------- Read more ---------- */
const initReadMore = () => {
  document.querySelectorAll('[data-ml-readmore]').forEach((wrap) => {
    if (wrap.dataset.ready) return;
    wrap.dataset.ready = 'true';
    const inner = wrap.firstElementChild;
    const toggle = wrap.querySelector('[data-ml-readmore-toggle]');
    if (!inner || !toggle) return;
    const lineHeight = parseFloat(getComputedStyle(inner).lineHeight) || 24;
    if (inner.scrollHeight <= lineHeight * 3.5) return;
    wrap.classList.add('is-clamped');
    toggle.hidden = false;
    toggle.addEventListener('click', () => {
      const clamped = wrap.classList.toggle('is-clamped');
      toggle.setAttribute('aria-expanded', clamped ? 'false' : 'true');
      toggle.textContent = clamped ? 'Read more' : 'Read less';
    });
  });
};

const init = () => {
  syncWishButtons();
  initReadMore();
  renderWishlistPage();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Cards re-rendered by Horizon (filters, infinite scroll, recommendations) need their heart state.
let syncQueued = false;
new MutationObserver(() => {
  if (syncQueued) return;
  syncQueued = true;
  requestAnimationFrame(() => {
    syncQueued = false;
    syncWishButtons();
    initReadMore();
  });
}).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
